import { DocumentsIcon, ChevronDownIcon, DownloadIcon, ExternalLinkIcon } from './ui'

export type DocumentAction = 'download' | 'view'

export interface PolicyDocument {
  id: string
  name: string
  description: string
  actions: DocumentAction[]
  /** When set, Download uses this URL (authenticated fetch). */
  downloadUrl?: string
  /** When set, View uses this URL (open in new tab via authenticated fetch). */
  viewUrl?: string
  /** When set, View opens this external URL in a new tab. */
  externalUrl?: string
}

interface PolicyDocumentsCardProps {
  documents: PolicyDocument[]
  isOpen?: boolean
  onToggle?: () => void
  onDownload?: (documentId: string) => void
  onView?: (documentId: string) => void
}

interface DocumentItemProps {
  document: PolicyDocument
  onDownload?: (documentId: string) => void
  onView?: (documentId: string) => void
  showDivider?: boolean
}

function DocumentItem({ document, onDownload, onView, showDivider = true }: DocumentItemProps) {
  const hasDownload = document.actions.includes('download')
  const hasView = document.actions.includes('view')
  const hasExternalLink = Boolean(document.externalUrl)
  const hasOnlyView = hasView && !hasDownload

  const handleViewClick = () => {
    if (document.externalUrl) {
      window.open(document.externalUrl, '_blank', 'noopener,noreferrer')
      return
    }
    onView?.(document.id)
  }

  return (
    <div
      className={`flex flex-col gap-[12px] items-start px-[8px] py-[16px] relative w-full${showDivider ? ' border-t border-border-secondary' : ''}`}
    >
      <div className="flex flex-col gap-[2px] items-start relative shrink-0 w-full">
        <p className="font-body font-medium text-md leading-md text-text-secondary-700 w-full">
          {document.name}
        </p>
        <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 w-full">
          {document.description}
        </p>
      </div>
      <div className="flex flex-wrap gap-[8px] items-start pt-[2px] relative shrink-0 w-full">
        {hasOnlyView || hasExternalLink ? (
          <button
            type="button"
            onClick={handleViewClick}
            className="bg-background-primary border border-border-primary border-solid flex gap-[4px] items-center justify-center overflow-clip px-[14px] py-[10px] relative rounded-md shadow-xs shrink-0 w-full"
          >
            <div className="overflow-clip relative shrink-0 size-5 text-text-tertiary-600">
              <ExternalLinkIcon />
            </div>
            <span className="font-body font-semibold text-sm leading-sm text-text-secondary-700">
              View
            </span>
          </button>
        ) : (
          <>
            {hasDownload && (
              <button
                type="button"
                onClick={() => onDownload?.(document.id)}
                className="bg-background-primary border border-border-primary border-solid flex flex-1 gap-[4px] items-center justify-center overflow-clip px-[14px] py-[10px] relative rounded-md shadow-xs shrink-0"
              >
                <div className="overflow-clip relative shrink-0 size-5 text-text-tertiary-600">
                  <DownloadIcon />
                </div>
                <span className="font-body font-semibold text-sm leading-sm text-text-secondary-700">
                  Download
                </span>
              </button>
            )}
            {hasView && !hasExternalLink && (
              <button
                type="button"
                onClick={handleViewClick}
                className="bg-background-primary border border-border-primary border-solid flex flex-1 gap-[4px] items-center justify-center overflow-clip px-[14px] py-[10px] relative rounded-md shadow-xs shrink-0"
              >
                <div className="overflow-clip relative shrink-0 size-5 text-text-tertiary-600">
                  <ExternalLinkIcon />
                </div>
                <span className="font-body font-semibold text-sm leading-sm text-text-secondary-700">
                  View
                </span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface PolicyDocumentsListProps {
  documents: PolicyDocument[]
  onDownload?: (documentId: string) => void
  onView?: (documentId: string) => void
}

/** Always-visible document list for the dedicated Documents tab page. */
export function PolicyDocumentsList({ documents, onDownload, onView }: PolicyDocumentsListProps) {
  return (
    <div className="bg-background-primary border border-border-secondary border-solid flex flex-col gap-[12px] items-start px-[16px] py-[12px] relative rounded-lg shadow-xs w-full">
      <div className="flex gap-[12px] items-center relative shrink-0 w-full">
        <div className="bg-background-tertiary overflow-clip relative rounded-full shrink-0 size-12">
          <div className="absolute left-3 top-3 overflow-clip size-6 text-text-tertiary-600">
            <DocumentsIcon />
          </div>
        </div>
        <p className="flex-1 font-display font-medium text-display-xs leading-[32px] text-text-primary-900 min-w-0 overflow-ellipsis overflow-hidden whitespace-nowrap">
          Documents
        </p>
      </div>
      <div className="flex flex-col items-start relative shrink-0 w-full">
        {documents.map((doc) => (
          <DocumentItem key={doc.id} document={doc} onDownload={onDownload} onView={onView} showDivider={false} />
        ))}
      </div>
    </div>
  )
}

export default function PolicyDocumentsCard({
  documents,
  isOpen = false,
  onToggle,
  onDownload,
  onView,
}: PolicyDocumentsCardProps) {
  return (
    <div className="bg-background-primary border border-border-secondary border-solid flex flex-col gap-[12px] items-start px-[16px] py-[12px] relative rounded-lg shadow-xs w-full">
      <button
        onClick={onToggle}
        className="flex gap-[12px] items-center relative shrink-0 w-full text-left"
        aria-expanded={isOpen}
      >
        <div className="bg-background-tertiary overflow-clip relative rounded-full shrink-0 size-12">
          <div className="absolute left-3 top-3 overflow-clip size-6 text-text-tertiary-600">
            <DocumentsIcon />
          </div>
        </div>
        <p className="flex-1 font-display font-medium text-display-xs leading-[32px] text-text-primary-900 min-w-0 overflow-ellipsis overflow-hidden whitespace-nowrap">
          Documents
        </p>
        <div
          className={`overflow-clip relative shrink-0 size-4 text-text-tertiary-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          data-testid="chevron-icon"
        >
          <ChevronDownIcon />
        </div>
      </button>
      {isOpen && (
        <>
          <div className="h-px bg-border-primary relative shrink-0 w-full" />
          <div className="flex flex-col items-start relative shrink-0 w-full">
            {documents.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                onDownload={onDownload}
                onView={onView}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
